import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Eye,
  Heart,
  MessageCircle,
  Target,
  BarChart3,
  CheckCircle,
  AlertCircle,
  InfoIcon,
  Lightbulb,
  Zap,
  DollarSign,
  ExternalLink,
  Instagram,
  Video,
  Image,
  ImageOff,
  Lock,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  Navigation,
  MousePointerClick,
  LogOut,
  Users,
  User,
  UserPlus,
  Share2,
  Repeat2,
  Activity
} from 'lucide-react';
import { Card, Button } from '../ui';
import { socialMediaService } from '../../services/socialMediaService';
import { formatEMV } from '../../utils/emvCalculator';
import { SocialMediaPost } from '../../types/socialMedia';
import usePermissionsStore from '../../stores/permissionsStore';
import { useDrawerNavigation } from '../../contexts/DrawerNavigationContext';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

interface PostInsightsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  post: SocialMediaPost | null;
}

interface PostInsights {
  post: {
    postId: number;
    platformPostId: string;
    platform: {
      id: number;
      name: string;
      displayName: string;
    };
    account: {
      id: string;
      username: string;
      displayName: string;
      customer: {
        id: string;
        name: string;
      };
    };
    caption: string;
    postType: string;
    contentType: string;
    postUrl: string;
    postedAt: string;
    isSponsored: boolean;
    isAd: boolean;
    collaboratorUsername?: string | null;
    hashtags: string[];
    media: Array<{
      mediaId: number;
      mediaType: string;
      mediaUrl: string;
      displayOrder: number;
      width: number;
      height: number;
      durationSeconds?: number;
    }>;
    stats: {
      likesCount: number;
      commentsCount: number;
      sharesCount: number;
      viewsCount: number;
      savesCount: number;
      emv: number;
      snapshotDate: string;
      // Platform-native metrics (e.g. Instagram Story reach/replies/navigation breakdown/link_clicks).
      extendedMetrics?: Record<string, any> | null;
    };
  };
  insights: {
    total: number;
    items: Array<{
      id: number;
      type: string;
      category: string;
      title: string;
      description: string;
      priority: 'high' | 'medium' | 'low';
      confidenceLevel: number;
      potentialImpact: 'high' | 'medium' | 'low';
      implementationDifficulty: 'low' | 'medium' | 'high';
      estimatedEffortHours: number;
      actionableRecommendations: string[] | {
        schedule?: string;
        frequency?: string;
        tools?: string[];
      };
      supportingData: {
        [key: string]: any;
      };
      successMetrics: string[];
      benchmarkData: {
        industryAverage: number;
        yourAverage: number;
      } | null;
      trendIndicators: {
        trend: string;
        strength: string;
      } | null;
      seasonalFactors: {
        season: string;
        impact: string;
      } | null;
      createdAt: string;
    }>;
  };
  contentAnalysis: {
    total: number;
    items: Array<{
      id: number;
      analysisType: string;
      analysisModel: string;
      analysisVersion: string | null;
      sentimentScore: number;
      sentimentLabel: string | null;
      confidenceScore: number;
      primaryTopics: string[] | null;
      keywords: string[] | null;
      toneAnalysis: {
        enthusiasm: number;
        confidence: number;
        professionalism: number;
      } | null;
      engagementPrediction: {
        predictedLikes?: number;
        predictedComments?: number;
        predictedShares?: number;
        predicted_likes?: number;
        predicted_comments?: number;
        predicted_shares?: number;
        confidence?: number;
      } | null;
      contentCategories: string[] | null;
      targetAudience: {
        ageGroup?: string;
        interests?: string[];
        demographics?: string;
      } | null;
      contentQualityScore: number;
      readabilityScore: number;
      emotionalTriggers: string[] | null;
      callToAction: string | null;
      brandSafetyScore: number;
      complianceFlags: string[] | null;
      processingTimeMs: number;
      createdAt: string;
    }>;
  };
}

const PostInsightsDrawer: React.FC<PostInsightsDrawerProps> = ({ isOpen, onClose, post }) => {
  const { hasFeature } = usePermissionsStore();
  const { openBrandDetails, setTabMemory } = useDrawerNavigation();
  const [insights, setInsights] = useState<PostInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'insights' | 'analysis'>('overview');
  
  // Brands tab state
  interface BrandAppearance {
    startTimeSeconds: number;
    endTimeSeconds: number;
    durationSeconds: number;
    confidence: number;
    frameCount: number;
    boundingBoxes: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      confidence: number;
      frameTimeMs: number;
    }>;
  }

  // Support both old and new brand data structures
  interface BrandData {
    brandName: string;
    // New structure
    appearanceCount?: number;
    totalDurationSeconds?: number;
    detectionCount?: number;
    avgConfidence?: number;
    // Old structure
    confidence?: number;
    appearances?: BrandAppearance[];
  }

  const [mediaData, setMediaData] = useState<string | null>(null);
  const [brandsData, setBrandsData] = useState<BrandData[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mediaContentType, setMediaContentType] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    if (isOpen && post) {
      setThumbError(false);
      loadPostInsights();
    }
  }, [isOpen, post]);

  // Load media and brands data when brands tab is selected
  useEffect(() => {
    if (activeTab === 'brands' && post && hasFeature('video_analysis')) {
      loadMediaAndBrands();
    }
  }, [activeTab, post, hasFeature]);

  const loadMediaAndBrands = async () => {
    if (!post?.id) return;

    setMediaLoading(true);
    setMediaError(null);

    try {
      // Extract postId and partitionKey from post
      const postId = parseInt(post.id);
      const partitionKey = post.partitionKey || 1;

      const response = await socialMediaService.getPostMedia(postId, partitionKey, true);


      if (response.success && response.data && response.data.media && response.data.media.length > 0) {
        const firstMedia = response.data.media[0];
        setBrandsData(firstMedia.brands || []);
        setMediaContentType(firstMedia.contentType || null);

        // Only set media if backend confirmed it's available
        if (firstMedia.mediaAvailable && firstMedia.media) {
          setMediaData(firstMedia.media);
        } else {
          setMediaData(null);
        }
      } else if (response.success === false && response.data === undefined) {
        // No media rows at all — still check for brand data in other media items
        setMediaData(null);
      } else {
        setMediaError('Failed to load media and brands data');
      }
    } catch (error) {
      setMediaError('Failed to load media and brands data');
    } finally {
      setMediaLoading(false);
    }
  };

  const loadPostInsights = async () => {
    if (!post) return;

    try {
      setLoading(true);
      setError(null);
      const postId = parseInt(post.id);
      const response = await socialMediaService.getPostInsightsByPostId(postId);
      setInsights(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load post insights');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="h-4 w-4" />;
      case 'medium':
        return <InfoIcon className="h-4 w-4" />;
      case 'low':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <InfoIcon className="h-4 w-4" />;
    }
  };

  const getSentimentColor = (score: number) => {
    if (score >= 0.6) return 'text-green-600';
    if (score >= 0.3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 z-[9999]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-[10000] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                  <Eye className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Post Insights & Analysis</h2>
                  <p className="text-sm text-gray-500">AI-powered insights and content analysis</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center border-b border-gray-200 px-4">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'brands', label: 'Brands' },
                { id: 'insights', label: 'Insights' },
                { id: 'analysis', label: 'Analysis' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="text-gray-900">Loading insights...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="p-6">
                  <Card className="p-6 border-red-200 bg-red-50">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <p className="text-red-700">{error}</p>
                    </div>
                  </Card>
                </div>
              )}

              {insights && (
                <div className="p-4 space-y-4">
                  {activeTab === 'overview' && (
                    <div className="space-y-4">
                      {/* Post Summary — compact: thumbnail left, info right */}
                      <Card className="p-4">
                        <div className="flex items-start space-x-3">
                          {/* Thumbnail */}
                          <div className="flex-shrink-0 w-32 aspect-video rounded-lg overflow-hidden bg-gray-100 relative">
                            {!thumbError && insights.post.account.customer?.id ? (
                              <img
                                src={socialMediaService.getThumbnailUrl(insights.post.postId, insights.post.account.customer.id)}
                                alt="Post thumbnail"
                                className="w-full h-full object-cover"
                                crossOrigin="use-credentials"
                                onError={() => setThumbError(true)}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-0.5">
                                <ImageOff className="h-5 w-5" />
                                <span className="text-[9px]">No preview</span>
                              </div>
                            )}
                            {/* Post-type badge overlay */}
                            {insights.post.postType && (
                              <span className="absolute top-1 left-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-black/60 text-white capitalize">
                                {insights.post.postType}
                              </span>
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-1">
                              {insights.post.platform.name === 'instagram' ? (
                                <Instagram className="h-4 w-4 text-pink-500 flex-shrink-0" />
                              ) : (
                                <div className="h-4 w-4 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                  <span className="text-[9px] font-bold text-gray-600">TT</span>
                                </div>
                              )}
                              <h3 className="text-sm font-semibold text-gray-900 truncate">
                                @{insights.post.collaboratorUsername || insights.post.account.username}
                              </h3>
                              <span className="text-xs text-gray-500">•</span>
                              <span className="text-xs text-gray-500">{formatDate(insights.post.postedAt)}</span>
                              {insights.post.isSponsored && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">
                                  Sponsored
                                </span>
                              )}
                              {insights.post.isAd && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                                  Ad
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-700 mb-2 line-clamp-2">{insights.post.caption}</p>
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <div className="flex items-center">
                                {insights.post.contentType === 'video' ? (
                                  <Video className="h-3.5 w-3.5 mr-1" />
                                ) : (
                                  <Image className="h-3.5 w-3.5 mr-1" />
                                )}
                                {insights.post.contentType}
                              </div>
                              <a
                                href={insights.post.postUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-purple-600 hover:text-purple-700"
                              >
                                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                View Post
                              </a>
                            </div>
                          </div>
                        </div>
                      </Card>

                      {/* Performance Metrics */}
                      {insights.post.postType === 'story' && insights.post.stats?.extendedMetrics ? (
                        (() => {
                          const em = insights.post.stats.extendedMetrics as Record<string, any>;
                          const nav = (em.navigation || {}) as Record<string, any>;
                          const navTotal = nav.total ?? 0;
                          const pct = (v: number) => (navTotal > 0 ? Math.round((v / navTotal) * 100) : 0);
                          const navItems = [
                            { key: 'tap_forward', label: 'Tap forward', value: nav.tap_forward ?? 0, color: 'bg-blue-500', Icon: ChevronRight },
                            { key: 'tap_back', label: 'Tap back', value: nav.tap_back ?? 0, color: 'bg-amber-500', Icon: ChevronLeft },
                            { key: 'tap_exit', label: 'Tap exit', value: nav.tap_exit ?? 0, color: 'bg-red-500', Icon: LogOut },
                            { key: 'swipe_forward', label: 'Swipe forward', value: nav.swipe_forward ?? 0, color: 'bg-purple-500', Icon: ChevronsRight },
                          ];
                          const reachItems = [
                            { label: 'Reach', value: em.reach, Icon: Users },
                            { label: 'Total views', value: em.total_views ?? em.views, Icon: Eye },
                            { label: 'Replies', value: em.replies, Icon: MessageCircle },
                            { label: 'Shares', value: em.shares, Icon: Share2 },
                            { label: 'Reposts', value: em.reposts, Icon: Repeat2 },
                            { label: 'Profile visits', value: em.profile_visits, Icon: User },
                            { label: 'Follows', value: em.follows, Icon: UserPlus },
                            { label: 'Profile activity', value: em.profile_activity, Icon: Activity },
                            { label: 'Total interactions', value: em.total_interactions, Icon: Sparkles },
                          ];
                          const heroes = [
                            { label: 'EMV', value: formatEMV(insights.post.stats.emv), Icon: DollarSign, bg: 'bg-green-100', fg: 'text-green-600' },
                            { label: 'Views', value: formatNumber(em.views ?? insights.post.stats.viewsCount), Icon: Eye, bg: 'bg-purple-100', fg: 'text-purple-600' },
                            { label: 'Reach', value: formatNumber(em.reach ?? 0), Icon: Users, bg: 'bg-sky-100', fg: 'text-sky-600' },
                            { label: 'Link clicks', value: formatNumber(em.link_clicks ?? 0), Icon: MousePointerClick, bg: 'bg-blue-100', fg: 'text-blue-600' },
                          ];
                          return (
                            <div className="space-y-3">
                              {/* Story badge + hero metrics */}
                              <div className="flex items-center gap-1.5">
                                <Instagram className="h-3.5 w-3.5 text-pink-500" />
                                <span className="text-xs font-semibold text-gray-700">Story performance</span>
                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-pink-50 text-pink-700">Enhanced API</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                {heroes.map((h) => (
                                  <Card key={h.label} className="px-3 py-2">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="text-xs font-medium text-gray-500">{h.label}</div>
                                        <div className="text-base font-bold text-gray-900">{h.value}</div>
                                      </div>
                                      <div className={`p-1.5 ${h.bg} rounded-md`}>
                                        <h.Icon className={`h-3.5 w-3.5 ${h.fg}`} />
                                      </div>
                                    </div>
                                  </Card>
                                ))}
                              </div>

                              {/* Navigation breakdown */}
                              <Card className="px-3 py-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-1.5">
                                    <Navigation className="h-3.5 w-3.5 text-purple-600" />
                                    <span className="text-xs font-semibold text-gray-700">Navigation</span>
                                  </div>
                                  <span className="text-xs font-bold text-gray-900">{formatNumber(navTotal)} total</span>
                                </div>
                                <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 mb-3">
                                  {navItems.map((i) => (
                                    navTotal > 0 && i.value > 0 ? (
                                      <div key={i.key} className={i.color} style={{ width: `${(i.value / navTotal) * 100}%` }} title={`${i.label}: ${i.value}`} />
                                    ) : null
                                  ))}
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                  {navItems.map((i) => (
                                    <div key={i.key} className="flex items-center justify-between text-xs">
                                      <span className="flex items-center gap-1 text-gray-500"><i.Icon className="h-3 w-3" />{i.label}</span>
                                      <span className="font-medium text-gray-900">{formatNumber(i.value)}<span className="text-gray-400 ml-1">({pct(i.value)}%)</span></span>
                                    </div>
                                  ))}
                                </div>
                              </Card>

                              {/* Reach & interactions */}
                              <Card className="px-3 py-3">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <BarChart3 className="h-3.5 w-3.5 text-gray-500" />
                                  <span className="text-xs font-semibold text-gray-700">Reach &amp; interactions</span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                  {reachItems.map((i) => (
                                    <div key={i.label} className="flex items-center justify-between text-xs">
                                      <span className="flex items-center gap-1 text-gray-500"><i.Icon className="h-3 w-3" />{i.label}</span>
                                      <span className="font-semibold text-gray-900">{formatNumber(i.value ?? 0)}</span>
                                    </div>
                                  ))}
                                </div>
                              </Card>
                            </div>
                          );
                        })()
                      ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <Card className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-500">EMV</div>
                              <div className="text-base font-bold text-gray-900">{formatEMV(insights.post.stats.emv)}</div>
                            </div>
                            <div className="p-1.5 bg-green-100 rounded-md">
                              <DollarSign className="h-3.5 w-3.5 text-green-600" />
                            </div>
                          </div>
                        </Card>
                        <Card className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-500">Likes</div>
                              <div className="text-base font-bold text-gray-900">{formatNumber(insights.post.stats.likesCount)}</div>
                            </div>
                            <div className="p-1.5 bg-red-100 rounded-md">
                              <Heart className="h-3.5 w-3.5 text-red-600" />
                            </div>
                          </div>
                        </Card>
                        <Card className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-500">Comments</div>
                              <div className="text-base font-bold text-gray-900">{formatNumber(insights.post.stats.commentsCount)}</div>
                            </div>
                            <div className="p-1.5 bg-blue-100 rounded-md">
                              <MessageCircle className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                          </div>
                        </Card>
                        <Card className="px-3 py-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-medium text-gray-500">Views</div>
                              <div className="text-base font-bold text-gray-900">{formatNumber(insights.post.stats.viewsCount)}</div>
                            </div>
                            <div className="p-1.5 bg-purple-100 rounded-md">
                              <Eye className="h-3.5 w-3.5 text-purple-600" />
                            </div>
                          </div>
                        </Card>
                      </div>
                      )}

                    </div>
                  )}

                  {activeTab === 'brands' && (
                    <div className="space-y-4">
                      {!hasFeature('video_analysis') ? (
                        /* Upgrade Content */
                        <Card className="p-8 text-center">
                          <div className="flex justify-center mb-6">
                            <div className="p-4 bg-gray-100 rounded-full">
                              <Lock className="h-12 w-12 text-gray-400" />
                            </div>
                          </div>
                          
                          <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            Upgrade to Access Brand Detection
                          </h2>
                          
                          <p className="text-gray-600 mb-6">
                            Brand detection is not available in your current subscription tier. 
                            Upgrade your plan to unlock AI-powered brand detection in your video content.
                          </p>
                          
                          <div className="space-y-3 text-left mb-8">
                            <h3 className="font-semibold text-gray-900">With Brand Detection, you can:</h3>
                            <ul className="space-y-2 text-gray-600">
                              <li className="flex items-start">
                                <Eye className="h-5 w-5 text-purple-500 mr-2 mt-0.5" />
                                <span>Automatically detect brands in video content</span>
                              </li>
                              <li className="flex items-start">
                                <BarChart3 className="h-5 w-5 text-purple-500 mr-2 mt-0.5" />
                                <span>Track brand exposure duration and frequency</span>
                              </li>
                              <li className="flex items-start">
                                <Target className="h-5 w-5 text-purple-500 mr-2 mt-0.5" />
                                <span>Monitor confidence levels of brand detections</span>
                              </li>
                              <li className="flex items-start">
                                <Sparkles className="h-5 w-5 text-purple-500 mr-2 mt-0.5" />
                                <span>View precise bounding boxes around detected brands</span>
                              </li>
                            </ul>
                          </div>
                          
                          <Button 
                            size="lg" 
                            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                          >
                            Upgrade Your Plan
                          </Button>
                          
                          <p className="text-sm text-gray-500 mt-4">
                            Contact your administrator or support team for upgrade options
                          </p>
                        </Card>
                      ) : (
                        /* Brands Content */
                        <>
                          {/* Media Viewer */}
                          <Card className="overflow-hidden">
                            <div className="relative bg-black">
                              {mediaLoading ? (
                                <div className="flex items-center justify-center h-64">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                                    <p className="text-white">Loading media...</p>
                                  </div>
                                </div>
                              ) : mediaError ? (
                                <div className="flex flex-col items-center justify-center h-64">
                                  <div className="text-red-400 mb-2">
                                    <AlertCircle className="h-8 w-8" />
                                  </div>
                                  <p className="text-white mb-4">{mediaError}</p>
                                  <Button onClick={loadMediaAndBrands} variant="outline">
                                    Try Again
                                  </Button>
                                </div>
                              ) : mediaData ? (
                                <div className="relative">
                                  {mediaContentType?.includes('video') ? (
                                    <video
                                      controls
                                      className="w-full max-h-96 object-contain"
                                      src={mediaData}
                                      onError={(e) => {
                                        console.error('Video failed to load:', e);
                                        console.error('Video src:', mediaData);
                                        setMediaError('Video failed to load');
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={mediaData}
                                      alt="Post content"
                                      className="w-full max-h-96 object-contain"
                                      onError={() => {
                                        setMediaData(null);
                                      }}
                                    />
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center h-48 bg-gray-100 rounded-lg">
                                  <div className="text-gray-400 mb-2">
                                    {mediaContentType?.includes('video') || post?.contentType?.includes('video') ? (
                                      <Video className="h-8 w-8" />
                                    ) : (
                                      <Image className="h-8 w-8" />
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-500">Media not available for this post</p>
                                </div>
                              )}
                            </div>
                          </Card>

                          {/* Brands List */}
                          <Card className="p-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                              <Sparkles className="h-4 w-4 text-purple-500 mr-2" />
                              Detected Brands
                            </h3>

                            {brandsData.length === 0 ? (
                              <div className="text-center py-6">
                                <Sparkles className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                                <h4 className="text-sm font-medium text-gray-900 mb-1">No Brands Detected</h4>
                                <p className="text-xs text-gray-600">
                                  No brand detections found in this content. Brand analysis data will appear here when available.
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {brandsData.map((brand, index) => {
                                  // Determine which structure we're dealing with
                                  const isNewStructure = brand.avgConfidence !== undefined;
                                  const confidence = isNewStructure ? brand.avgConfidence! : brand.confidence!;

                                  return (
                                    <div
                                      key={index}
                                      className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all duration-200 group"
                                      onClick={() => {
                                        // Remember current tab before navigating
                                        setTabMemory(`post-${post?.id}`, 'brands');
                                        
                                        // Prepare brand data for BrandDetailsDrawer
                                        const brandData = {
                                          brandName: brand.brandName,
                                          postCount: 0, // Will be loaded by BrandDetailsDrawer
                                          totalDurationSeconds: brand.totalDurationSeconds || 0,
                                          totalDetections: brand.detectionCount || 0,
                                          totalEmv: '$0.00', // Will be loaded by BrandDetailsDrawer
                                          avgConfidence: confidence
                                        };
                                        
                                        // Navigate to brand details
                                        openBrandDetails(brandData, true);
                                        // Don't call onClose() here - let navigation handle it
                                      }}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-700">{brand.brandName}</h4>
                                        <div className="flex items-center space-x-2">
                                          <div className="text-xs text-gray-500">
                                            {Math.round(confidence * 100)}% confidence
                                          </div>
                                          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
                                        </div>
                                      </div>

                                      {isNewStructure ? (
                                        // New structure layout
                                        <div className="grid grid-cols-3 gap-3 text-xs">
                                          <div className="text-center">
                                            <div className="font-medium text-gray-900">{brand.appearanceCount}</div>
                                            <div className="text-gray-500">Appearances</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="font-medium text-gray-900">{brand.totalDurationSeconds!.toFixed(1)}s</div>
                                            <div className="text-gray-500">Total Duration</div>
                                          </div>
                                          <div className="text-center">
                                            <div className="font-medium text-gray-900">{Math.round(confidence * 100)}%</div>
                                            <div className="text-gray-500">Avg Confidence</div>
                                          </div>
                                        </div>
                                      ) : (
                                        // Old structure layout with appearances
                                        brand.appearances && brand.appearances.length > 0 && (
                                          <div className="space-y-2">
                                            <h5 className="text-sm font-medium text-gray-700">Appearances:</h5>
                                            {brand.appearances.map((appearance, appIndex) => (
                                              <div key={appIndex} className="bg-gray-50 rounded p-3 text-sm">
                                                <div className="flex items-center justify-between">
                                                  <span className="text-gray-700">
                                                    {appearance.startTimeSeconds.toFixed(1)}s - {appearance.endTimeSeconds.toFixed(1)}s
                                                  </span>
                                                  <span className="text-gray-600">
                                                    {appearance.frameCount} frames
                                                  </span>
                                                </div>
                                                <div className="text-gray-600 mt-1">
                                                  Duration: {appearance.durationSeconds.toFixed(1)}s | 
                                                  Confidence: {Math.round(appearance.confidence * 100)}%
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </Card>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'insights' && (
                    <div className="space-y-4">
                      {(!insights.insights?.items || insights.insights.items.length === 0) ? (
                        <Card className="p-8 text-center">
                          <Lightbulb className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                          <h3 className="text-sm font-medium text-gray-900 mb-1">Insights Not Yet Available</h3>
                          <p className="text-xs text-gray-500">AI insights are still being generated for this post.</p>
                        </Card>
                      ) : (
                        insights.insights.items.map((insight) => (
                          <Card key={insight.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-start space-x-3">
                                <div className={`flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(insight.priority)}`}>
                                  {getPriorityIcon(insight.priority)}
                                  <span className="ml-1 capitalize">{insight.priority}</span>
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{insight.title}</h4>
                                  <p className="text-xs text-gray-700 mb-3">{insight.description}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500">Confidence</div>
                                <div className="text-sm font-semibold text-purple-600">
                                  {Math.round(insight.confidenceLevel * 100)}%
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-3">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Impact</div>
                                <div className="text-sm font-semibold text-blue-900 capitalize">{insight.potentialImpact}</div>
                              </div>
                              <div className="bg-green-50 p-3 rounded-lg">
                                <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Effort</div>
                                <div className="text-sm font-semibold text-green-900">{insight.estimatedEffortHours}h</div>
                              </div>
                              <div className="bg-orange-50 p-3 rounded-lg">
                                <div className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Difficulty</div>
                                <div className="text-sm font-semibold text-orange-900 capitalize">{insight.implementationDifficulty}</div>
                              </div>
                            </div>

                            {/* Supporting Data */}
                            {insight.supportingData && Object.keys(insight.supportingData).length > 0 && (
                              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-2">Supporting Data</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  {Object.entries(insight.supportingData).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                      <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                                      <span className="text-gray-900 font-medium">
                                        {typeof value === 'object' && value !== null 
                                          ? JSON.stringify(value) 
                                          : String(value)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {insight.actionableRecommendations && (
                              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                                <h5 className="text-sm font-semibold text-purple-900 mb-2 flex items-center">
                                  <Zap className="h-4 w-4 mr-1" />
                                  Actionable Recommendations
                                </h5>
                                <div className="space-y-2 text-sm text-purple-800">
                                  {Array.isArray(insight.actionableRecommendations) ? (
                                    <ul className="list-disc list-inside space-y-1">
                                      {insight.actionableRecommendations.map((rec: string, index: number) => (
                                        <li key={index}>{rec}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <>
                                      {insight.actionableRecommendations.schedule && (
                                        <p><strong>Schedule:</strong> {insight.actionableRecommendations.schedule}</p>
                                      )}
                                      {insight.actionableRecommendations.frequency && (
                                        <p><strong>Frequency:</strong> {insight.actionableRecommendations.frequency}</p>
                                      )}
                                      {insight.actionableRecommendations.tools && insight.actionableRecommendations.tools.length > 0 && (
                                        <p><strong>Tools:</strong> {insight.actionableRecommendations.tools.join(', ')}</p>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {insight.successMetrics && insight.successMetrics.length > 0 && (
                              <div className="border-t pt-4">
                                <h5 className="text-sm font-semibold text-gray-900 mb-2">Success Metrics</h5>
                                <div className="flex flex-wrap gap-2">
                                  {insight.successMetrics.map((metric, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                                    >
                                      <Target className="h-3 w-3 mr-1" />
                                      {metric}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </Card>
                        ))
                      )}
                    </div>
                  )}

                  {activeTab === 'analysis' && (
                    <div className="space-y-4">
                      {(!insights.contentAnalysis?.items || insights.contentAnalysis.items.length === 0) ? (
                        <Card className="p-8 text-center">
                          <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                          <h3 className="text-sm font-medium text-gray-900 mb-1">Analysis Is Not Yet Available</h3>
                          <p className="text-xs text-gray-500">AI analysis is still being generated for this post.</p>
                        </Card>
                      ) : (
                        (() => {
                          // Group analysis by type for better organization, excluding engagement_prediction
                          const groupedAnalysis = insights.contentAnalysis.items
                            .filter((analysis: any) => analysis.analysisType !== 'engagement_prediction')
                            .reduce((acc: any, analysis: any) => {
                              const type = analysis.analysisType || 'general';
                              if (!acc[type]) {
                                acc[type] = [];
                              }
                              acc[type].push(analysis);
                              return acc;
                            }, {});

                          // Define the desired order for analysis panels
                          const analysisOrder = ['radar_metrics', 'sentiment', 'topics', 'content_quality', 'brand_safety', 'tone', 'target_audience', 'call_to_action'];
                          const orderedAnalysisTypes = analysisOrder.filter(type => groupedAnalysis[type]);
                          const remainingTypes = Object.keys(groupedAnalysis).filter(type => !analysisOrder.includes(type));
                          const finalOrder = [...orderedAnalysisTypes, ...remainingTypes];

                          return (
                            <div className="space-y-4">
                              {finalOrder.map((analysisType) => (
                                <Card key={analysisType} className="p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-semibold text-gray-900 capitalize">
                                      {analysisType.replace('_', ' ')} Analysis
                                    </h4>
                                  </div>

                                  {/* Combine all data from analyses of this type */}
                                  {(() => {
                                    const analyses = groupedAnalysis[analysisType];
                                    const combinedAnalysis = analyses.reduce((combined: any, analysis: any) => {
                                      // Merge all non-null/non-empty fields
                                      Object.keys(analysis).forEach(key => {
                                        if (analysis[key] !== null && analysis[key] !== undefined && analysis[key] !== 0 && 
                                            (Array.isArray(analysis[key]) ? analysis[key].length > 0 : true)) {
                                          if (!combined[key] || combined[key] === null || combined[key] === 0 || 
                                              (Array.isArray(combined[key]) && combined[key].length === 0)) {
                                            combined[key] = analysis[key];
                                          }
                                        }
                                      });
                                      return combined;
                                    }, {});

                                    return (
                                      <div className="space-y-4">
                                        {/* Radar Metrics Analysis */}
                                        {combinedAnalysis.radarChartData && (
                                          <div className="space-y-6">
                                            {/* Performance Overview Radar */}
                                            {combinedAnalysis.radarChartData.radar_chart && (
                                              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                                                <h5 className="text-base font-semibold text-indigo-900 mb-3">Performance Overview</h5>
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                  {/* Radar Chart */}
                                                  <div className="h-64">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                      <RadarChart data={combinedAnalysis.radarChartData.radar_chart.dimensions}>
                                                        <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                                                        <PolarAngleAxis 
                                                          dataKey="axis" 
                                                          tick={{ fill: '#6b7280', fontSize: 11 }}
                                                        />
                                                        <PolarRadiusAxis 
                                                          angle={90} 
                                                          domain={[0, 100]} 
                                                          tick={{ fill: '#9ca3af', fontSize: 10 }}
                                                          tickCount={5}
                                                        />
                                                        <Radar
                                                          name="Performance"
                                                          dataKey="value"
                                                          stroke="#6366f1"
                                                          fill="#6366f1"
                                                          fillOpacity={0.3}
                                                          strokeWidth={2}
                                                        />
                                                        <Tooltip 
                                                          contentStyle={{
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            fontSize: '12px'
                                                          }}
                                                          formatter={(value: any) => `${value}/100`}
                                                        />
                                                      </RadarChart>
                                                    </ResponsiveContainer>
                                                  </div>
                                                  {/* Metrics Summary */}
                                                  <div className="space-y-3">
                                                    <div className="text-center mb-4">
                                                      <div className="text-3xl font-bold text-indigo-600">
                                                        {combinedAnalysis.radarChartData.radar_chart.overall_score}/100
                                                      </div>
                                                      <div className="text-sm text-gray-600">Overall Score</div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 italic">
                                                      {combinedAnalysis.radarChartData.radar_chart.narrative}
                                                    </p>
                                                  </div>
                                                </div>
                                                {/* Dimension Details */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                                                  {combinedAnalysis.radarChartData.radar_chart.dimensions.map((dim: any, idx: number) => (
                                                    <div key={idx} className="bg-white p-3 rounded-lg border border-indigo-100">
                                                      <div className="flex items-center justify-between mb-1">
                                                        <span className="text-sm font-medium text-gray-900">{dim.axis}</span>
                                                        <span className="text-sm font-bold text-indigo-600">{dim.value}%</span>
                                                      </div>
                                                      <p className="text-xs text-gray-600">{dim.description}</p>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {/* Content Strategy Radar */}
                                            {combinedAnalysis.radarChartData.content_strategy && (
                                              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                                                <h5 className="text-base font-semibold text-purple-900 mb-3">Content Strategy Analysis</h5>
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                  {/* Radar Chart */}
                                                  <div className="h-64">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                      <RadarChart data={combinedAnalysis.radarChartData.content_strategy.dimensions}>
                                                        <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                                                        <PolarAngleAxis 
                                                          dataKey="axis" 
                                                          tick={{ fill: '#6b7280', fontSize: 11 }}
                                                        />
                                                        <PolarRadiusAxis 
                                                          angle={90} 
                                                          domain={[0, 100]} 
                                                          tick={{ fill: '#9ca3af', fontSize: 10 }}
                                                          tickCount={5}
                                                        />
                                                        <Radar
                                                          name="Strategy"
                                                          dataKey="value"
                                                          stroke="#a855f7"
                                                          fill="#a855f7"
                                                          fillOpacity={0.3}
                                                          strokeWidth={2}
                                                        />
                                                        <Tooltip 
                                                          contentStyle={{
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            fontSize: '12px'
                                                          }}
                                                          formatter={(value: any) => `${value}/100`}
                                                        />
                                                      </RadarChart>
                                                    </ResponsiveContainer>
                                                  </div>
                                                  {/* Strategy Summary */}
                                                  <div className="space-y-3">
                                                    <div className="text-center mb-4">
                                                      <div className="text-3xl font-bold text-purple-600">
                                                        {combinedAnalysis.radarChartData.content_strategy.overall_score}/100
                                                      </div>
                                                      <div className="text-sm text-gray-600">Strategy Score</div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 italic">
                                                      {combinedAnalysis.radarChartData.content_strategy.narrative}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Audience Engagement Radar */}
                                            {combinedAnalysis.radarChartData.audience_engagement && (
                                              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
                                                <h5 className="text-base font-semibold text-emerald-900 mb-3">Audience Engagement Analysis</h5>
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                  {/* Radar Chart */}
                                                  <div className="h-64">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                      <RadarChart data={combinedAnalysis.radarChartData.audience_engagement.dimensions}>
                                                        <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                                                        <PolarAngleAxis 
                                                          dataKey="axis" 
                                                          tick={{ fill: '#6b7280', fontSize: 11 }}
                                                        />
                                                        <PolarRadiusAxis 
                                                          angle={90} 
                                                          domain={[0, 100]} 
                                                          tick={{ fill: '#9ca3af', fontSize: 10 }}
                                                          tickCount={5}
                                                        />
                                                        <Radar
                                                          name="Engagement"
                                                          dataKey="value"
                                                          stroke="#10b981"
                                                          fill="#10b981"
                                                          fillOpacity={0.3}
                                                          strokeWidth={2}
                                                        />
                                                        <Tooltip 
                                                          contentStyle={{
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '8px',
                                                            fontSize: '12px'
                                                          }}
                                                          formatter={(value: any) => `${value}/100`}
                                                        />
                                                      </RadarChart>
                                                    </ResponsiveContainer>
                                                  </div>
                                                  {/* Engagement Summary */}
                                                  <div className="space-y-3">
                                                    <div className="text-center mb-4">
                                                      <div className="text-3xl font-bold text-emerald-600">
                                                        {combinedAnalysis.radarChartData.audience_engagement.overall_score}/100
                                                      </div>
                                                      <div className="text-sm text-gray-600">Engagement Score</div>
                                                    </div>
                                                    <p className="text-sm text-gray-700 italic">
                                                      {combinedAnalysis.radarChartData.audience_engagement.narrative}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* Sentiment Analysis */}
                                        {combinedAnalysis.sentimentScore > 0 && (
                                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                                            <h5 className="text-base font-semibold text-blue-900 mb-3">Sentiment Analysis</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                                              <div>
                                                <div className={`text-2xl font-bold mb-1 ${getSentimentColor(combinedAnalysis.sentimentScore)}`}>
                                                  {Math.round(combinedAnalysis.sentimentScore * 100)}%
                                                </div>
                                                <div className="text-sm text-gray-600">Sentiment Score</div>
                                              </div>
                                              {combinedAnalysis.sentimentLabel && (
                                                <div>
                                                  <div className="text-2xl font-bold text-gray-900 capitalize mb-1">
                                                    {combinedAnalysis.sentimentLabel}
                                                  </div>
                                                  <div className="text-sm text-gray-600">Overall Sentiment</div>
                                                </div>
                                              )}
                                              {combinedAnalysis.confidenceScore > 0 && (
                                                <div>
                                                  <div className={`text-2xl font-bold mb-1 ${getScoreColor(combinedAnalysis.confidenceScore)}`}>
                                                    {Math.round(combinedAnalysis.confidenceScore * 100)}%
                                                  </div>
                                                  <div className="text-sm text-gray-600">Confidence</div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Content Quality */}
                                        {combinedAnalysis.contentQualityScore > 0 && (
                                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                                            <h5 className="text-base font-semibold text-green-900 mb-3">Content Quality</h5>
                                            <div className="text-center">
                                              <div className={`text-2xl font-bold mb-1 ${getScoreColor(combinedAnalysis.contentQualityScore)}`}>
                                                {Math.round(combinedAnalysis.contentQualityScore * 100)}%
                                              </div>
                                              <div className="text-sm text-gray-600">Quality Score</div>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Brand Safety */}
                                        {combinedAnalysis.brandSafetyScore > 0 && (
                                          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                                            <h5 className="text-base font-semibold text-orange-900 mb-3">Brand Safety</h5>
                                            <div className="text-center">
                                              <div className={`text-2xl font-bold mb-1 ${getScoreColor(combinedAnalysis.brandSafetyScore)}`}>
                                                {Math.round(combinedAnalysis.brandSafetyScore * 100)}%
                                              </div>
                                              <div className="text-sm text-gray-600">Safety Score</div>
                                            </div>
                                          </div>
                                        )}

                                        {/* Primary Topics */}
                                        {combinedAnalysis.primaryTopics && combinedAnalysis.primaryTopics.length > 0 && (
                                          <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4">
                                            <h5 className="text-base font-semibold text-purple-900 mb-3">Primary Topics</h5>
                                            <div className="flex flex-wrap gap-2">
                                              {combinedAnalysis.primaryTopics.map((topic: string, index: number) => (
                                                <span
                                                  key={index}
                                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
                                                >
                                                  {topic}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Keywords and Categories */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                          {/* Keywords */}
                                          {combinedAnalysis.keywords && combinedAnalysis.keywords.length > 0 && (
                                            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-4">
                                              <h5 className="text-base font-semibold text-yellow-900 mb-3">Keywords</h5>
                                              <div className="flex flex-wrap gap-2">
                                                {combinedAnalysis.keywords.map((keyword: string, index: number) => (
                                                  <span
                                                    key={index}
                                                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200"
                                                  >
                                                    {keyword}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Content Categories */}
                                          {combinedAnalysis.contentCategories && combinedAnalysis.contentCategories.length > 0 && (
                                            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4">
                                              <h5 className="text-base font-semibold text-teal-900 mb-3">Content Categories</h5>
                                              <div className="flex flex-wrap gap-2">
                                                {combinedAnalysis.contentCategories.map((category: string, index: number) => (
                                                  <span
                                                    key={index}
                                                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200"
                                                  >
                                                    {category}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>

                                        {/* Tone Analysis and Target Audience */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                          {/* Tone Analysis */}
                                          {combinedAnalysis.toneAnalysis && (
                                            <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-4">
                                              <h5 className="text-base font-semibold text-rose-900 mb-3">Tone Analysis</h5>
                                              <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm text-gray-600">Enthusiasm</span>
                                                  <span className="text-sm font-semibold text-gray-900">{Math.round(combinedAnalysis.toneAnalysis.enthusiasm * 100)}%</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm text-gray-600">Confidence</span>
                                                  <span className="text-sm font-semibold text-gray-900">{Math.round(combinedAnalysis.toneAnalysis.confidence * 100)}%</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm text-gray-600">Professionalism</span>
                                                  <span className="text-sm font-semibold text-gray-900">{Math.round(combinedAnalysis.toneAnalysis.professionalism * 100)}%</span>
                                                </div>
                                              </div>
                                            </div>
                                          )}

                                          {/* Target Audience */}
                                          {combinedAnalysis.targetAudience && (
                                            <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-lg p-4">
                                              <h5 className="text-base font-semibold text-slate-900 mb-3">Target Audience</h5>
                                              <div className="text-sm text-gray-700 space-y-1">
                                                {combinedAnalysis.targetAudience.ageGroup && (
                                                  <p><span className="font-medium">Age Group:</span> {combinedAnalysis.targetAudience.ageGroup}</p>
                                                )}
                                                {combinedAnalysis.targetAudience.demographics && (
                                                  <p><span className="font-medium">Demographics:</span> {combinedAnalysis.targetAudience.demographics}</p>
                                                )}
                                                {combinedAnalysis.targetAudience.interests && combinedAnalysis.targetAudience.interests.length > 0 && (
                                                  <p><span className="font-medium">Interests:</span> {combinedAnalysis.targetAudience.interests.join(', ')}</p>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>

                                        {/* Call to Action */}
                                        {combinedAnalysis.callToAction && (
                                          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4">
                                            <h5 className="text-base font-semibold text-emerald-900 mb-3">Call to Action</h5>
                                            <div className="text-sm text-gray-700">
                                              {combinedAnalysis.callToAction}
                                            </div>
                                          </div>
                                        )}

                                      </div>
                                    );
                                  })()}
                                </Card>
                              ))}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PostInsightsDrawer;